import React, { useState, useEffect } from "react";
import { useChatHistories } from "../hooks/useChatHistories";
import { isTerminalState } from "../hooks/useSwarmSessionHelpers";
import { RiDeleteBinLine, RiPencilLine } from "react-icons/ri";
import { WorkflowIcon } from "./subcomponents/WorkflowIconPicker";

const INITIAL_VISIBLE = 3;
const SWARM_PAGE_SIZE = 20;

function ChatHistoryList({
  API_URL,
  user,
  onSelectConversation,
  activeConversationId,
  onDeleteConversation,
  onRenameConversation,
  isDeleting,
  isRenaming,
  refreshTrigger,
  loadingConversationIds = new Set(),
  pendingConversations = [],
  onHistoriesFetched = null,
  justCompletedIds = new Set(),
  swarmSessions = [],
  pendingSwarmSessions = [],
  loadingSwarmSessionIds = new Set(),
  onModeChange = null,
  activeMode = "ask",
  onSelectSwarmSession,
  onDeleteSwarmSession,
  onRenameSwarmSession,
  isDeletingSwarm,
  isRenamingSwarm,
  activeSwarmSessionId,
  swarmSessionsLoading,
  workflowRuns = [],
  runningWorkflowRunIds,
  workflowList = [],
  onRenameWorkflowRun,
  onDeleteWorkflowRun,
  onSelectWorkflowRun,
  activeWorkflowRunId = null,
}) {
  const userId = user?.id;

  const {
    histories,
    loading,
    loadingMore,
    error,
    hasMore,
    fetchMore,
  } = useChatHistories(API_URL, userId, refreshTrigger);

  useEffect(() => {
    if (onHistoriesFetched) {
      onHistoriesFetched(histories);
    }
  }, [histories, onHistoriesFetched]);

  const fetchedIds = new Set(histories.map(h => h.conversation_id));
  const mergedHistories = [
    ...pendingConversations
      .filter(p => !fetchedIds.has(p.conversation_id))
      .sort((a, b) => b.last_modified.localeCompare(a.last_modified)),
    ...histories,
  ];

  const [activeView, setActiveView] = useState("conversations");

  useEffect(() => {
    if (activeSwarmSessionId) setActiveView("swarm");
  }, [activeSwarmSessionId]);

  useEffect(() => {
    if (activeConversationId) setActiveView("conversations");
  }, [activeConversationId]);

  useEffect(() => {
    if (pendingSwarmSessions.length > 0) setActiveView("swarm");
  }, [pendingSwarmSessions.length]);

  useEffect(() => {
    if (activeMode === "swarm") setActiveView("swarm");
    else if (activeMode === "workflow") setActiveView("workflows");
    else setActiveView("conversations");
  }, [activeMode]);

  const [convExpanded, setConvExpanded] = useState(false);
  const [convEditingId, setConvEditingId] = useState(null);
  const [convEditingName, setConvEditingName] = useState("");

  // Float the active conversation to position 0; rest stay in natural order.
  // When a different item becomes active it floats up, the previous one
  // settles back to its chronological position automatically.
  const sortedHistories = (() => {
    if (!activeConversationId) return mergedHistories;
    const idx = mergedHistories.findIndex(
      h => h.conversation_id === activeConversationId
    );
    if (idx <= 0) return mergedHistories;
    const active = mergedHistories[idx];
    return [
      active,
      ...mergedHistories.filter(
        h => h.conversation_id !== activeConversationId
      ),
    ];
  })();

  const visibleHistories = convExpanded
    ? sortedHistories
    : sortedHistories.slice(0, INITIAL_VISIBLE);

  const [swarmExpanded, setSwarmExpanded] = useState(false);
  const [swarmEditingId, setSwarmEditingId] = useState(null);
  const [swarmEditingName, setSwarmEditingName] = useState("");
  const [swarmOffset, setSwarmOffset] = useState(0);

  const [localSwarmSessions, setLocalSwarmSessions] = useState([]);
  const [swarmHasMore, setSwarmHasMore] = useState(true);
  const [swarmLoadingMore, setSwarmLoadingMore] = useState(false);

  useEffect(() => {
    const fetchedSwarmIds = new Set(swarmSessions.map(s => s.session_id));
    const pendingOnly = pendingSwarmSessions
      .filter(p => !fetchedSwarmIds.has(p.session_id))
      .sort((a, b) => (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || ''));
    setLocalSwarmSessions([...pendingOnly, ...swarmSessions]);
    setSwarmHasMore(swarmSessions.length === SWARM_PAGE_SIZE);
    setSwarmOffset(swarmSessions.length);
  }, [swarmSessions, pendingSwarmSessions]);

  // Float the active swarm session to position 0.
  const sortedSwarmSessions = (() => {
    if (!activeSwarmSessionId) return localSwarmSessions;
    const idx = localSwarmSessions.findIndex(
      s => s.session_id === activeSwarmSessionId
    );
    if (idx <= 0) return localSwarmSessions;
    const active = localSwarmSessions[idx];
    return [
      active,
      ...localSwarmSessions.filter(
        s => s.session_id !== activeSwarmSessionId
      ),
    ];
  })();

  const visibleSwarm = swarmExpanded
    ? sortedSwarmSessions
    : sortedSwarmSessions.slice(0, INITIAL_VISIBLE);

  // ---- Conversation handlers ----
  const handleConvDelete = (e, conversationId) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this conversation?")) {
      onDeleteConversation(conversationId);
    }
  };

  const handleConvRenameStart = (e, conversationId, currentName) => {
    e.stopPropagation();
    setConvEditingId(conversationId);
    setConvEditingName(currentName || "");
  };

  const handleConvNameSubmit = (e, conversationId) => {
    e.preventDefault();
    e.stopPropagation();
    if (convEditingName.trim()) onRenameConversation(conversationId, convEditingName);
    setConvEditingId(null);
    setConvEditingName("");
  };

  const handleConvKeyDown = (e) => {
    if (e.key === "Enter") handleConvNameSubmit(e, convEditingId);
    else if (e.key === "Escape") { setConvEditingId(null); setConvEditingName(""); }
  };

  const handleConvShowMore = async () => {
    if (!convExpanded) {
      setConvExpanded(true);
    } else if (hasMore) {
      await fetchMore();
    }
  };

  // ---- Swarm handlers ----
  const handleSwarmDelete = (e, sessionId) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this swarm session?")) {
      onDeleteSwarmSession && onDeleteSwarmSession(sessionId);
    }
  };

  const handleSwarmRenameStart = (e, sessionId, currentName) => {
    e.stopPropagation();
    setSwarmEditingId(sessionId);
    setSwarmEditingName(currentName || "");
  };

  const handleSwarmNameSubmit = (e, sessionId) => {
    e.preventDefault();
    e.stopPropagation();
    if (swarmEditingName.trim()) onRenameSwarmSession && onRenameSwarmSession(sessionId, swarmEditingName);
    setSwarmEditingId(null);
    setSwarmEditingName("");
  };

  const handleSwarmKeyDown = (e) => {
    if (e.key === "Enter") handleSwarmNameSubmit(e, swarmEditingId);
    else if (e.key === "Escape") { setSwarmEditingId(null); setSwarmEditingName(""); }
  };

  const handleSwarmShowMore = async () => {
    if (!swarmExpanded) {
      setSwarmExpanded(true);
    } else if (swarmHasMore && !swarmLoadingMore) {
      setSwarmLoadingMore(true);
      try {
        const res = await fetch(
          `${API_URL}/swarm/sessions?limit=${SWARM_PAGE_SIZE}&offset=${swarmOffset}`,
          { credentials: "include" },
        );
        if (res.ok) {
          const data = await res.json();
          const next = data.sessions || [];
          setLocalSwarmSessions((prev) => {
            const seen = new Set(prev.map(s => s.session_id));
            return [...prev, ...next.filter(s => !seen.has(s.session_id))];
          });
          setSwarmHasMore(next.length === SWARM_PAGE_SIZE);
          setSwarmOffset((o) => o + next.length);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setSwarmLoadingMore(false);
      }
    }
  };

  const actionBtnStyle = (disabled) => ({
    background: "transparent",
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    padding: "8px",
    color: "var(--secondary-text-color)",
    opacity: disabled ? 0.5 : 1,
  });

  const actionBtnActiveStyle = (disabled) => ({
    ...actionBtnStyle(disabled),
    color: "#fff",
  });

  const deleteBtnStyle = (disabled) => ({
    ...actionBtnStyle(disabled),
    color: "var(--error-text-color, #dc2626)",
  });

  const deleteBtnActiveStyle = (disabled) => ({
    ...deleteBtnStyle(disabled),
    color: "#fca5a5",
  });

  // ---- Conversation item renderer ----
  // Used for both the pinned active entry and the scrollable list.
  const renderConvItem = (h, itemKey) => {
    const isActive = h.conversation_id === activeConversationId;
    const isEditingThis = convEditingId === h.conversation_id;
    const isLoading =
      loadingConversationIds.has(h.conversation_id)
      || !!h.isPending
      || (h.status === 'running' && !justCompletedIds.has(h.conversation_id));

    return (
      <li
        key={itemKey}
        style={{
          padding: 0, margin: 0,
          background: isActive ? "var(--chat-history-active-bg)" : "transparent",
          borderBottom: "1px solid #eaeaea",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          cursor: isEditingThis ? "default" : "pointer",
        }}
        onClick={() => {
          if (!isEditingThis && onSelectConversation)
            onSelectConversation(h.conversation_id, h.status);
        }}
      >
        {isEditingThis ? (
          <form
            onSubmit={(e) => handleConvNameSubmit(e, h.conversation_id)}
            style={{ flex: 1, padding: "8px" }}
          >
            <input
              type="text"
              value={convEditingName}
              onChange={(e) => setConvEditingName(e.target.value)}
              onKeyDown={handleConvKeyDown}
              onBlur={() => setConvEditingId(null)}
              autoFocus
              style={{ width: "100%", boxSizing: "border-box" }}
            />
          </form>
        ) : (
          <button
            type="button"
            style={{
              flex: 1, width: "100%", textAlign: "left", padding: "8px",
              border: "none", background: "transparent", cursor: "pointer",
              display: "flex", alignItems: "center", gap: "6px",
            }}
          >
            {isLoading && (
              <span style={{
                display: "inline-block",
                width: "8px", height: "8px",
                borderRadius: "50%",
                border: "2px solid var(--primary-color, #636cff)",
                borderTopColor: "transparent",
                animation: "spin 0.75s linear infinite",
                flexShrink: 0,
              }} />
            )}
            <span style={{
              fontSize: 12,
              color: isActive ? "#fff" : "var(--secondary-text-color)",
              fontWeight: isActive ? 600 : 500,
              wordBreak: "break-all",
            }}>
              {h.name || h.conversation_id}
            </span>
          </button>
        )}
        <div style={{ display: "flex", alignItems: "center", paddingRight: "8px" }}>
          <button
            type="button"
            onClick={(e) => handleConvRenameStart(e, h.conversation_id, h.name)}
            disabled={isRenaming || isEditingThis || isLoading}
            title={isLoading ? "Cannot rename while generating" : "Rename"}
            style={
              isActive
                ? actionBtnActiveStyle(isRenaming || isEditingThis || isLoading)
                : actionBtnStyle(isRenaming || isEditingThis || isLoading)
            }
          >
            <RiPencilLine size={14} />
          </button>
          <button
            type="button"
            onClick={(e) => handleConvDelete(e, h.conversation_id)}
            disabled={isDeleting || isEditingThis || isLoading}
            title={isLoading ? "Cannot delete while generating" : "Delete"}
            style={
              isActive
                ? deleteBtnActiveStyle(isDeleting || isEditingThis || isLoading)
                : deleteBtnStyle(isDeleting || isEditingThis || isLoading)
            }
          >
            <RiDeleteBinLine size={14} />
          </button>
        </div>
      </li>
    );
  };

  // ---- Swarm item renderer ----
  const renderSwarmItem = (s, itemKey) => {
    const isActive = s.session_id === activeSwarmSessionId;
    const isEditingThis = swarmEditingId === s.session_id;
    const isSwarmLoading =
      loadingSwarmSessionIds.has(s.session_id)
      || (s.state != null && !isTerminalState(s.state));
    const isStarting = s.session_id?.startsWith('pending-');
    const displayName = s.name || s.question || s.session_id;
    const truncated = displayName.length > 48 ? displayName.slice(0, 48) + "…" : displayName;

    return (
      <li
        key={itemKey}
        style={{
          padding: 0, margin: 0,
          background: isActive ? "var(--chat-history-active-bg)" : "transparent",
          borderBottom: "1px solid #eaeaea",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          cursor: isEditingThis ? "default" : "pointer",
        }}
        onClick={() => {
          if (!isEditingThis && !isStarting && onSelectSwarmSession)
            onSelectSwarmSession(s);
        }}
      >
        {isEditingThis ? (
          <form
            onSubmit={(e) => handleSwarmNameSubmit(e, s.session_id)}
            style={{ flex: 1, padding: "8px" }}
          >
            <input
              type="text"
              value={swarmEditingName}
              onChange={(e) => setSwarmEditingName(e.target.value)}
              onKeyDown={handleSwarmKeyDown}
              onBlur={() => setSwarmEditingId(null)}
              autoFocus
              style={{ width: "100%", boxSizing: "border-box" }}
            />
          </form>
        ) : (
          <button
            type="button"
            style={{
              flex: 1, width: "100%", textAlign: "left", padding: "8px",
              border: "none", background: "transparent",
              cursor: isStarting ? "default" : "pointer",
              display: "flex", alignItems: "center", gap: "6px",
            }}
          >
            {isSwarmLoading && (
              <span style={{
                display: "inline-block",
                width: "8px", height: "8px",
                borderRadius: "50%",
                border: "2px solid var(--primary-color, #636cff)",
                borderTopColor: "transparent",
                animation: "spin 0.75s linear infinite",
                flexShrink: 0,
              }} />
            )}
            <div style={{
              fontSize: 12,
              color: isActive ? "#fff" : "var(--secondary-text-color)",
              fontWeight: isActive ? 600 : 500,
              wordBreak: "break-word",
            }}>
              {truncated}
            </div>
          </button>
        )}
        <div style={{ display: "flex", alignItems: "center", paddingRight: "8px" }}>
          <button
            type="button"
            onClick={(e) => handleSwarmRenameStart(e, s.session_id, s.name || s.question)}
            disabled={isRenamingSwarm || isEditingThis || isStarting}
            title={isStarting ? "Cannot rename while starting" : "Rename"}
            style={
              isActive
                ? actionBtnActiveStyle(isRenamingSwarm || isEditingThis || isStarting)
                : actionBtnStyle(isRenamingSwarm || isEditingThis || isStarting)
            }
          >
            <RiPencilLine size={14} />
          </button>
          <button
            type="button"
            onClick={(e) => handleSwarmDelete(e, s.session_id)}
            disabled={isDeletingSwarm || isEditingThis || isStarting}
            title={isStarting ? "Cannot delete while starting" : "Delete"}
            style={
              isActive
                ? deleteBtnActiveStyle(isDeletingSwarm || isEditingThis || isStarting)
                : deleteBtnStyle(isDeletingSwarm || isEditingThis || isStarting)
            }
          >
            <RiDeleteBinLine size={14} />
          </button>
        </div>
      </li>
    );
  };

  return (
    <div style={{ marginTop: 16 }}>
      {/* History type selector */}
      <div style={{ marginBottom: 12, position: 'relative' }}>
        <select
          value={activeView}
          onChange={(e) => {
            const v = e.target.value;
            setActiveView(v);
            if (v === "conversations") onModeChange && onModeChange("ask");
            else if (v === "swarm") onModeChange && onModeChange("swarm");
            else if (v === "workflows") onModeChange && onModeChange("workflow");
          }}
          style={{
            width: "100%",
            padding: "5px 28px 5px 8px",
            fontSize: 12,
            border: "1px solid var(--border-color, #ccc)",
            borderRadius: 4,
            background: "var(--input-background, transparent)",
            color: "var(--text-color)",
            cursor: "pointer",
            outline: "none",
            appearance: "none",
            WebkitAppearance: "none",
            MozAppearance: "none",
          }}
        >
          <option value="conversations">Conversations</option>
          <option value="swarm">Swarm Sessions</option>
          <option value="workflows">Workflow Runs</option>
        </select>
        <span style={{
          position: 'absolute',
          right: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: '1rem',
          color: 'var(--text-secondary, #888)',
          pointerEvents: 'none',
          lineHeight: 1,
        }}>▾</span>
      </div>

      {/* ---- Conversations view ---- */}
      {activeView === "conversations" && (
        <>
          {!userId && (
            <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
              Sign in to view chat history.
            </div>
          )}
          {userId && (
            <>
              {loading && mergedHistories.length === 0 && (
                <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>Loading...</div>
              )}
              {error && (
                <div style={{ fontSize: 12, color: "#b00020", marginTop: 8 }}>
                  Failed to load chat history.
                </div>
              )}
              {!loading && mergedHistories.length === 0 && !error && (
                <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
                  No conversations yet.
                </div>
              )}
              {mergedHistories.length > 0 && (
                <>
                  <ul style={{
                    listStyle: "none", padding: 0, marginTop: 0, marginBottom: 0,
                    maxHeight: "38vh",
                    overflowY: "auto",
                  }}>
                    {visibleHistories.map((h) =>
                      renderConvItem(h, h.conversation_id)
                    )}
                  </ul>

                  {(!convExpanded && mergedHistories.length > INITIAL_VISIBLE) ||
                    (convExpanded && hasMore) ? (
                    <div style={{ marginTop: 10 }}>
                      <button
                        type="button"
                        onClick={handleConvShowMore}
                        disabled={loadingMore}
                        style={{
                          fontSize: 12, padding: "6px 10px",
                          border: "1px solid #ddd", borderRadius: 4,
                          cursor: loadingMore ? "default" : "pointer",
                          opacity: loadingMore ? 0.6 : 1,
                        }}
                      >
                        {loadingMore ? "Loading..." : !convExpanded ? "Show More" : "Load More"}
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* ---- Swarm sessions view ---- */}
      {activeView === "swarm" && (
        <>
          {!userId && (
            <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
              Sign in to view swarm sessions.
            </div>
          )}
          {userId && swarmSessionsLoading && localSwarmSessions.length === 0 && (
            <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>Loading...</div>
          )}
          {userId && !swarmSessionsLoading && localSwarmSessions.length === 0 && (
            <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
              No swarm sessions yet.
            </div>
          )}
          {userId && localSwarmSessions.length > 0 && (
            <>
              <ul style={{
                listStyle: "none", padding: 0, margin: 0, marginTop: 0,
                maxHeight: "38vh",
                overflowY: "auto",
              }}>
                {visibleSwarm.map((s) => renderSwarmItem(s, s.session_id))}
              </ul>

              {(!swarmExpanded && localSwarmSessions.length > INITIAL_VISIBLE) ||
                (swarmExpanded && swarmHasMore) ? (
                <div style={{ marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={handleSwarmShowMore}
                    disabled={swarmLoadingMore}
                    style={{
                      fontSize: 12, padding: "6px 10px",
                      border: "1px solid #ddd", borderRadius: 4,
                      cursor: swarmLoadingMore ? "default" : "pointer",
                      opacity: swarmLoadingMore ? 0.6 : 1,
                    }}
                  >
                    {swarmLoadingMore ? "Loading..." : !swarmExpanded ? "Show More" : "Load More"}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </>
      )}

      {/* ---- Workflow runs view ---- */}
      {activeView === "workflows" && (
        <WorkflowRunsList
          runs={workflowRuns}
          runningWorkflowRunIds={runningWorkflowRunIds}
          workflowList={workflowList}
          onRename={onRenameWorkflowRun}
          onDelete={onDeleteWorkflowRun}
          onSelect={onSelectWorkflowRun}
          activeWorkflowRunId={activeWorkflowRunId}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Workflow runs list — one item per run, newest first
// ---------------------------------------------------------------------------

function WorkflowRunsList({
  runs,
  runningWorkflowRunIds,
  workflowList,
  onRename,
  onDelete,
  onSelect,
  activeWorkflowRunId,
}) {
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [expanded, setExpanded] = useState(false);

  // Float the active run to position 0; settles back when deactivated.
  const sortedRuns = (() => {
    if (!activeWorkflowRunId) return runs;
    const idx = runs.findIndex(r => r.id === activeWorkflowRunId);
    if (idx <= 0) return runs;
    const active = runs[idx];
    return [active, ...runs.filter(r => r.id !== activeWorkflowRunId)];
  })();

  const visibleRuns = expanded ? sortedRuns : sortedRuns.slice(0, INITIAL_VISIBLE);

  const resolveRun = (run) => {
    const wf = workflowList.find((w) => w.id === run.workflow_id);
    const icon = run.workflow_icon || wf?.icon || null;

    let displayName = run.name;
    if (!displayName && run.input_json && typeof run.input_json === 'object') {
      const vals = Object.values(run.input_json).filter(Boolean);
      if (vals.length > 0) displayName = String(vals[0]);
    }
    if (!displayName) displayName = `Run ${new Date(run.created_at).toLocaleDateString()}`;

    const truncated = displayName.length > 38
      ? displayName.slice(0, 38) + '…'
      : displayName;

    return { icon, displayName, truncated };
  };

  const handleRenameStart = (e, run) => {
    e.stopPropagation();
    const { displayName } = resolveRun(run);
    setEditingId(run.id);
    setEditingName(run.name || displayName);
  };

  const handleRenameSubmit = async (e, run) => {
    e.preventDefault();
    const trimmed = editingName.trim();
    if (trimmed && onRename) {
      await onRename(run.workflow_id, run.id, trimmed);
    }
    setEditingId(null);
  };

  const handleDelete = async (e, run) => {
    e.stopPropagation();
    if (!window.confirm('Delete this workflow run? This cannot be undone.')) return;
    if (onDelete) await onDelete(run.workflow_id, run.id);
  };

  const actionBtnStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--secondary-text-color, #888)',
    padding: '2px 4px',
    borderRadius: 3,
    display: 'flex',
    alignItems: 'center',
    opacity: 0.7,
    transition: 'opacity 0.15s',
  };

  const deleteBtnStyle = {
    ...actionBtnStyle,
    color: 'var(--error-text-color, #dc2626)',
  };

  const renderRunItem = (run, itemKey) => {
    const { icon, truncated } = resolveRun(run);
    const isEditing = editingId === run.id;
    const isActive = run.id === activeWorkflowRunId;
    const isRunning =
      runningWorkflowRunIds?.has(run.id)
      || run.status === 'pending'
      || run.status === 'running';

    return (
      <li
        key={itemKey}
        style={{
          padding: 0, margin: 0,
          borderBottom: '1px solid var(--border-color, #eaeaea)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: isActive ? 'var(--chat-history-active-bg)' : 'transparent',
        }}
      >
        {isEditing ? (
          <form
            onSubmit={(e) => handleRenameSubmit(e, run)}
            style={{ flex: 1, padding: '8px' }}
          >
            <input
              type="text"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setEditingId(null); }}
              onBlur={() => setEditingId(null)}
              autoFocus
              style={{ width: '100%', boxSizing: 'border-box' }}
            />
          </form>
        ) : (
          <div
            role={onSelect ? 'button' : undefined}
            tabIndex={onSelect ? 0 : undefined}
            onClick={onSelect ? () => onSelect(run) : undefined}
            onKeyDown={
              onSelect
                ? (e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(run); }
                : undefined
            }
            style={{
              flex: 1, padding: '8px',
              display: 'flex', alignItems: 'center', gap: 6,
              minWidth: 0,
              cursor: onSelect ? 'pointer' : 'default',
            }}
          >
            {isRunning ? (
              <div
                className="loading-spinner"
                style={{
                  width: 11, height: 11, flexShrink: 0,
                  border: isActive
                    ? '2px solid rgba(255,255,255,0.3)'
                    : '2px solid rgba(99,108,255,0.25)',
                  borderLeftColor: isActive ? '#fff' : 'var(--primary-color, #636cff)',
                }}
              />
            ) : (
              <WorkflowIcon
                iconKey={icon}
                size={13}
                color={isActive ? '#fff' : 'var(--secondary-text-color, #888)'}
              />
            )}
            <div style={{
              fontSize: 12,
              color: isActive ? '#fff' : 'var(--secondary-text-color, #888)',
              fontWeight: isActive ? 600 : 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {truncated}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', paddingRight: 8, gap: 2 }}>
          <button
            type="button"
            style={isActive ? { ...actionBtnStyle, color: 'rgba(255,255,255,0.8)' } : actionBtnStyle}
            title="Rename run"
            onClick={(e) => handleRenameStart(e, run)}
          >
            <RiPencilLine size={13} />
          </button>
          <button
            type="button"
            style={isActive ? { ...deleteBtnStyle, color: 'rgba(255,255,255,0.8)' } : deleteBtnStyle}
            title="Delete run"
            onClick={(e) => handleDelete(e, run)}
          >
            <RiDeleteBinLine size={13} />
          </button>
        </div>
      </li>
    );
  };

  if (runs.length === 0) {
    return (
      <div style={{ fontSize: 12, color: '#888', padding: '8px 0' }}>
        No workflow runs yet.
      </div>
    );
  }

  return (
    <>
      <ul style={{
        listStyle: 'none', padding: 0, margin: 0,
        maxHeight: "38vh", overflowY: "auto",
      }}>
        {visibleRuns.map((run) => renderRunItem(run, run.id))}
      </ul>

      {runs.length > INITIAL_VISIBLE && (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            style={{
              fontSize: 12, padding: '6px 10px',
              border: '1px solid #ddd', borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            {expanded ? 'Show Less' : 'Show More'}
          </button>
        </div>
      )}
    </>
  );
}

export default ChatHistoryList;
